import React, {useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import {useAppSelector} from "@hooks/redux-hooks/hooks";
import {selectAzienda, selectPeriodo, selectSitePreferences, selectUser} from "@commons/core.slice";
import Loading from "@components/dataTable/Loading";
import {usePagination} from "@components/dataTable/usePagination";
import {useCalendarioChiusure, usePutSingolaRichiestaWF, useSingolaRichiestaWF} from "@commons/services/presenze_api/dashboard.queries";
import {FPNetDate, Periodo, PeriodoType} from "@commons/types";
import {useTabellaWF} from "@commons/services/presenze_api/workflow.queries";
import {AziendaRecord} from "@commons/mapping/AziendaRecord";
import {AzioniMassiveType, useMonitorWFTable, usePatchOpeMassivaMonitor} from "@commons/services/presenze_api/monitorWF.queries";
import {getMonitorWFTable, MonitorWFApiParams} from "@commons/services/presenze_api/monitorWF.api";
import {DateTime} from "luxon";
import {ContatoriRichiesteWF} from "../workflow/comps/ContatoriRichiesteWF";
import {RichiesteWfFilter} from "./FiltroRichiesteMonitorWF";
import {TableRichiesteWF} from "../dashboardWF/TableRichiesteWF";
import IntervalloSelectFilter from "../../../features/varie/intervalloSelectFilter";
import {Elaborato, MonitorWfFilter, Stati, Validita} from "@commons/filters";
import {useQueryFilter} from "@components/dataTable/customHooks";
import {ModalPanelForm} from "@components/Modal2023";
import BannerWF from "../dashboardWF/comps/BannerWF";
import DashboardDetailWF from "../dashboardWF/wf.dashboard.detail";
import {PresenzeCausaleWFRecord} from "@commons/mapping/PresenzeCausaleWF";
import {toast} from "react-toastify";
import {formatDt} from "@commons/utils/util";
import {cloneDeep} from "lodash";
import {UserCRUDRecord} from "@commons/mapping/UserCRUDRecord";
import ConfirmModal from "@components/ConfirmModal";
import {IconAlertTriangle, IconBan, IconCheck, IconSettings, IconTableExport} from "@tabler/icons-react";

export interface MonitorWfForm extends Omit<MonitorWfFilter, 'intervallo'> {
    intervallo: { startDate: string | null, endDate: string | null }
}

export const MonitorWorkflow: React.FC = () => {
    const periodoRecord: Periodo = useAppSelector(selectPeriodo);

    // isEmployee mi serve per NASCONDERE il tasto "RESETTA ELABORATO" agli EMPLOYEE, in attesa dell'implementazione dei GRANT!!!!!
    const user: UserCRUDRecord | null = useAppSelector(selectUser); // User corrente
    const isEmployee = user?.userClass === 'EMPLOYEE';
    const [selected, setSelected] = useState<number[]>([]);
    const aziendaRecord: AziendaRecord | null = useAppSelector(selectAzienda);// Azienda corrente

    const pagination = usePagination('', 1, 10);

    // Prelevo le sitePreferences per impostare il filtro la prima volta
    const sitePreferences = useAppSelector(selectSitePreferences);

    let preferencesMonitorFilter: MonitorWfFilter = sitePreferences.monitorWFFilter;
    if (preferencesMonitorFilter && preferencesMonitorFilter.intervallo.startDate && preferencesMonitorFilter.intervallo.endDate) {
        // Converto le date di preferencesMonitorFilter, nel timezone italiano! Altrimenti la startDate è un giorno indietro. Converto anche endDate per averla nello stesso formato di startDate!
        const stringStartDate = preferencesMonitorFilter.intervallo.startDate.toString();
        const stringEndDate = preferencesMonitorFilter.intervallo.endDate.toString();
        const startDate = DateTime.fromISO(stringStartDate, {zone: 'Europe/Rome'}).toJSDate()
        const endDate = DateTime.fromISO(stringEndDate).toJSDate()

        // Ridefinisco l'oggetto, inserendo startDate convertita con timezone corretto, ed endDate --> newPreferencesMonitorFilter lo utilizzo nei defaultParams per inizializzare il form del filtro!!!
        preferencesMonitorFilter = {
            ...preferencesMonitorFilter,
            intervallo: {...preferencesMonitorFilter.intervallo, startDate, endDate}
        }
    } else {
        // Se NON ho startDate, imposto 'p' come periodo predefinito per il filtro!
        const firstDay = new Date(periodoRecord.anno, periodoRecord.mese, 1); // Primo giorno del mese del periodo predefinito
        const lastDay = new Date(periodoRecord.anno, periodoRecord.mese, 0); // Ultimo giorno del mese del periodo predefinito
        // todo da verificare xchè 0 e xchè il codice seguente non andava bene?!?
        // const firstDay = DateTime.fromObject({year: periodoRecord.anno, month: periodoRecord.mese, day: 1});
        // const lastDay=DateTime.local(periodoRecord.anno,periodoRecord.mese,1).endOf('month');
        const json = {
            periodo: 'p' as PeriodoType,
            intervallo: {startDate: firstDay, endDate: lastDay},
        }
        preferencesMonitorFilter = MonitorWfFilter.factory(json);
    }

    const newPreferencesMonitorFilter: MonitorWfFilter = cloneDeep(preferencesMonitorFilter);

    const startDate = DateTime.fromJSDate(newPreferencesMonitorFilter.intervallo.startDate!).toFormat('yyyyLLdd');
    const endDate = DateTime.fromJSDate(newPreferencesMonitorFilter.intervallo.endDate!).toFormat('yyyyLLdd');
    const defaultFormData = {...newPreferencesMonitorFilter, intervallo: {...newPreferencesMonitorFilter.intervallo, startDate, endDate}}

    // ---------- FORM PER IL FILTRO PER PERIODO ---------- NB: passo newPreferencesMonitorFilter perche' a differenza di preferencesMonitorFilter, ho startDate impostata nel timezone IT!!!
    const formIntervalloFilter = useForm<MonitorWfForm>({defaultValues: defaultFormData});

    const {register, resetField, watch, getValues, setValue, control} = formIntervalloFilter;

    // ---------- FORM PER IL MODAL PANEL DELLA RICHIESTA ----------
    const hookForm = useForm<PresenzeCausaleWFRecord>({mode: 'onChange'});
    const {reset: resetAsyncFormModalPanel, control: controlModalPanel, setValue: setValModalPanel} = hookForm;

    // Gestione della singola richiesta (per ModalPanel)
    const [URI, setNextURI] = useState<string | null>(null);
    const [deleteRichiesta, setNextDeleteRichiesta] = useState<boolean>(false);
    const singolaRichiesta = useSingolaRichiestaWF(URI);

    // Carico tabella impianto WF
    const {data: tabellaWF} = useTabellaWF(aziendaRecord ? {idDominio: aziendaRecord.idDominio!, idTabella: aziendaRecord.idTabellaWF} : null);

    // Recupero le generalita' della tabella di raggruppamento --> serviranno per caricare i dati della select del raggruppamento! (vedi componente <RichiesteWfFilter/>)
    const [idTipoTabella, setIdTipoTabella] = useState<number | null>(null);
    //console.log(selected);
    const checkTabella = tabellaWF && tabellaWF.idTipoTabella;
    useEffect(() => {
        if (tabellaWF && (tabellaWF.idTipoTabella || tabellaWF.idTipoTabella === 0)) {
            setIdTipoTabella(tabellaWF.idTipoTabella);
        }
    }, [tabellaWF, checkTabella, aziendaRecord?.idAziendaCDIC]);

    // Per valorizzare il form all'apertura del MODAL
    useEffect(() => {
        // Come da documentazione https://react-hook-form.com/docs/useform/reset
        // values object An optional object to reset form values, and it's recommended to provide the entire defaultValues when supplied.
        resetAsyncFormModalPanel({...PresenzeCausaleWFRecord.factory(), ...singolaRichiesta.data});
        if (deleteRichiesta) {
            setValModalPanel('wf.annullata', true);
        }
    }, [deleteRichiesta, setValModalPanel, resetAsyncFormModalPanel, singolaRichiesta.data])

    /* Prelevo dal form i valori che mi servono per ricaricare la tabella\eseguire la query */
    const stati: Stati = watch('stati');
    const statiElaborato: Elaborato = watch('statiElaborato');
    const validita: Validita = watch('validita');
    const causale = watch('causale');
    const codTabella = watch('codTabella');
    const filterDataDa = watch('intervallo.startDate');
    const filterDataA = watch('intervallo.endDate');
    const checkCodTabella = codTabella === '' || codTabella === null;
    /* Da mapping, codTabella e idTipoTabella possono essere null, quindi testo se !==null, perchè in quel caso devo passare "undefined" - paramsMonitorWFTable */
    const idTab = (checkCodTabella || idTipoTabella === null) ? undefined : idTipoTabella;
    const codTab = checkCodTabella ? undefined : codTabella;

    const paramsMonitorWFTable: MonitorWFApiParams = {
        idAzienda: aziendaRecord?.idAzienda,
        idAziendaMaster: aziendaRecord?.idAziendaMaster,
        dataDa: filterDataDa,
        dataA: filterDataA,
        stati,
        statiElaborato,
        validita,
        causale: causale === '' ? undefined : causale,
        idTipoTabella: idTab,
        codTabella: codTab,
        start: pagination.getStart(),
        length: pagination.pageLength,
        search: {value: pagination.search},
    } as MonitorWFApiParams;

    /* ----- TABELLA MONITOR WF ----- */
    const {
        data: monitorData, isLoading: isLoadingMonitor, isFetching: isFetchingMonitor, isRefetching: isRefetchingMonitor
    } = useMonitorWFTable(paramsMonitorWFTable);
    const checkStatusQuery = isLoadingMonitor || isFetchingMonitor || isRefetchingMonitor;

    const [periodo, setNextPeriodo] = useState<Periodo>(useAppSelector(selectPeriodo));


    const data1 = DateTime.fromFormat(filterDataDa!, 'yyyyLLdd');
    const data2 = DateTime.fromFormat(filterDataA!, 'yyyyLLdd');
    const {isMonitorWFEditabile, isSameYear, hasCalendario} = useCalendarioChiusure(aziendaRecord?.idAziendaMaster, data1, data2);

    const disableBtns = !isMonitorWFEditabile(data1.toJSDate(), data2.toJSDate());
    console.log('disableBtns', disableBtns)
    const {filter, setFilter: setFilterData, skip} = useQueryFilter<MonitorWFApiParams>(paramsMonitorWFTable, pagination);

    const mutationSingolaRichiesta = usePutSingolaRichiestaWF(URI);
    const loadResource = (uri: string) => {
        setNextDeleteRichiesta(false);
        setNextURI(uri);
    }
    const deleteResource = (uri: string) => {
        setNextDeleteRichiesta(true);
        setNextURI(uri);
    }
    const onCancelEdit = () => {
        setNextURI(null);
    }
    const saveResource = (causale: PresenzeCausaleWFRecord) => {
        mutationSingolaRichiesta.mutateAsync(causale).then(() => {
            toast('Richiesta aggiornata!', {type: 'success'});
        });
    }

    const [showModalConfirm, setShowModalConfirm] = useState<boolean>(false);
    const [azione, setAzione] = useState<AzioniMassiveType>(null);

    const mutation = usePatchOpeMassivaMonitor(paramsMonitorWFTable,
        (dettagli) => toast(`Aggiornamento massivo eseguito! ${dettagli}`, {type: 'success'}),
        () => toast(`Errore in aggiornamento richieste!`, {type: 'error'}));

    const numRichieste = selected.length ? selected.length : (monitorData && monitorData.details) ? monitorData.details.count0 + monitorData.details.count1 : '0';

    /**
     * Gestisce il popup per confermare l'azione massiva e imposta l'azione massiva
     * @param azioneMassiva tipo di azione massiva da eseguire
     */
    function azioneMassivaConfirm(azioneMassiva: AzioniMassiveType) {
        setShowModalConfirm(true);
        setAzione(azioneMassiva);
    }

    const submitOpeMassiva = function (azione: AzioniMassiveType) {
        setShowModalConfirm(false);

        switch (azione) {
            case 'ACCETTA':
                mutation.mutateAsync({tipo: 'ACCETTA', richieste: selected.length ? selected : null});
                return 0;
            case 'RESPINGI':
                mutation.mutateAsync({tipo: 'RESPINGI', richieste: selected.length ? selected : null});
                return 0;
            case 'RESETTA':
                mutation.mutateAsync({tipo: 'RESETTA', richieste: null});
                return 0;
        }
    }

    /** getTableTitle()
     * Funzione per convertire le date per l'intestazione della tabella e
     * generare la stringa corretta in base al "periodo" selezionato
     */
    const getTableTitle = function () {
        const stringaTitle = 'Elenco richieste';
        if (filterDataDa && filterDataA) {
            const newDataDa = formatDt(filterDataDa, 'dd/LL/yyyy');
            const newDataA = formatDt(filterDataA, 'dd/LL/yyyy');

            switch (formIntervalloFilter.getValues('periodo')) {
                case 'b':
                case 't':
                case 'q':
                case 's':
                case 'p':
                case 'custom':
                    return `${stringaTitle} dal ${newDataDa} al ${newDataA}`
                case 'm':
                    return `${stringaTitle} di ${formatDt(filterDataDa, 'LLLL yyyy')}`
                case 'today':
                    return `${stringaTitle} di oggi, ${formatDt(filterDataDa, 'EEEE dd/LL/yyyy')}`
                case 'cy':
                    return `${stringaTitle} dell'anno ${formatDt(filterDataDa, 'yyyy')}`
                case 'fy':
                    return `${stringaTitle} dell'anno ${formatDt(filterDataDa, 'yyyy')}`
                default:
                    return `${stringaTitle} dal ${newDataDa} al ${newDataA}`;
            }
        } else {
            return stringaTitle;
        }
    }

    useEffect(() => {
        setShowModalConfirm(false);
        //console.log(monitorData)
    }, [monitorData]);

    const showAlert = !isSameYear && hasCalendario;

    return <>
        <Loading loading={checkStatusQuery}>
            <div className="mt-3">
                {/* CONTATORI */}
                <div className="flex flex-row justify-end">
                    <ContatoriRichiesteWF contatoriDetails={monitorData ? monitorData.details : undefined} page='monitorWF'/>
                </div>
                {/* FILTRO */}
                <div className="bg-white p-2 my-2">
                    <form onSubmit={() => {
                    }}>{/*formIntervalloFilter.handleSubmit(onSubmit)*/}
                        <RichiesteWfFilter tabellaWF={tabellaWF}
                                           setValue={setValue} getValues={getValues} resetField={resetField} register={register}/>
                        <IntervalloSelectFilter hook={formIntervalloFilter}
                            // isValid={isDateRangeValid}
                        />
                    </form>
                </div>

                {/* Se ho applicato il filtro del periodo e ci sono richieste da visualizzare */}
                <>
                    <TableRichiesteWF data={monitorData} loadResource={loadResource} deleteResource={deleteResource} URI={URI} pagination={pagination}
                                      page="monitorWF"
                                      tableTitle={getTableTitle()}
                                      isEditabile={(rifDate: FPNetDate) => isMonitorWFEditabile(data1.toJSDate(), data2.toJSDate(), DateTime.fromISO(rifDate!).toJSDate())}
                                      alertEditabile={showAlert &&
                                          <p className="text-red-600 font-semibold">N.B. Il periodo non permette di determinare se le informazioni sono editabili!</p>}
                                      selected={selected} setSelected={setSelected}
                                      showFuoriTermine={!showAlert}
                    /> {/* ATTENZIONE: alertEditabile --> FPNet2 ng-show="calendarioChiusure===null"*/}

                    <div className="flex items-center mt-3">
                        Legenda:&nbsp;<span className="text-sky-400">dati da Gestione Presenze</span>
                    </div>

                    <div className="flex items-center justify-end gap-x-4 mt-3">
                        {/*BTN EXPORT*/}
                        <button className="btnPrimary inline-flex" disabled={checkStatusQuery} onClick={() => {
                            const newFilter: MonitorWFApiParams = {...paramsMonitorWFTable, mode: 'export2excel'};
                            getMonitorWFTable(newFilter).then((res) => {
                                if (res) {
                                    /*const URI = res.data && res.data.URI ? res.data.URI : '???';
                                    customToastWithLink('richieste WF', URI);*/
                                    toast.success(<p>Export <i className="font-bold">richieste WF</i> eseguito, il file &egrave; stato salvato nello Spool.</p>)
                                }
                            }).catch((err) => {
                                toast.error('Errore nell\'esportazione delle richieste di WF! Ripovare. ' + err);
                            })
                        }}>
                            <IconTableExport size={18}/>&nbsp;Export
                        </button>

                        {/* BTNS AZIONI MASSIVE - azioneMassivaConfirm() - apre il modal e visualizza il messaggio di conferma dell'operazione in base al tipo di azione */}
                        {/* TODO: QUANDO SONO IMPLEMENTATI I GRANT, SOSTITUIRE hidden, E TESTARE I GRANT ANCHE NEGLI ALTRI BTNS!!! */}
                        <button className="btnPrimary" hidden={isEmployee} onClick={() => {
                            azioneMassivaConfirm('RESETTA')
                        }} disabled={!!URI || disableBtns}>
                            <IconSettings size={18}/>&nbsp;Resetta elaborato
                        </button>
                        <button className="btnSuccess" onClick={() => {
                            azioneMassivaConfirm('ACCETTA')
                        }} disabled={!!URI || disableBtns}>
                            <IconCheck size={18}/>&nbsp;{selected.length === 0 ? 'Accetta tutte' : `Accetta ${selected.length}`}
                        </button>
                        <button className="btnDanger" onClick={() => {
                            azioneMassivaConfirm('RESPINGI')
                        }} disabled={!!URI || disableBtns}>
                            <IconBan size={18}/>&nbsp;{selected.length === 0 ? 'Respingi tutte' : `Respingi ${selected.length}`}
                        </button>
                    </div>
                </>

                {/* Modal Panel per modifica richiesta */}
                {singolaRichiesta.data && (URI !== null) &&
                    <ModalPanelForm isOpen={!!singolaRichiesta.data} formControl={controlModalPanel} handleDismiss={onCancelEdit}
                                    header={<BannerWF richiesta={singolaRichiesta.data} lavoratore={singolaRichiesta.data}/>}>
                        <DashboardDetailWF
                            hook={hookForm}
                            richiesta={singolaRichiesta.data} periodo={periodo}
                            deleteRichiesta={deleteRichiesta}
                            onCancel={onCancelEdit}
                            onSave={saveResource}
                            page="monitorWF"
                            lavoratore={singolaRichiesta.data}
                            hhMancanti={0} hhPreviste={null} hhWeekMedie={0}
                            isEditabile={isMonitorWFEditabile(data1.toJSDate(), data2.toJSDate(), DateTime.fromISO(singolaRichiesta.data.giornoDt!).toJSDate())}
                        />
                    </ModalPanelForm>
                }

                {/* Modal Panel per CONFERMA dell'operazione massiva */}
                <ConfirmModal open={showModalConfirm} onCancel={() => setShowModalConfirm(false)} onConfirm={() => submitOpeMassiva(azione)}
                              children={<div className="sm:flex sm:items-start px-4 pt-4">
                                  <div className="mx-auto flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-lightWarning sm:mx-0 sm:h-12 sm:w-12">
                                      <IconAlertTriangle className="h-9 w-9 text-amber-500"/>
                                  </div>
                                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                      <h3 className="text-2xl font-medium leading-6 text-amber-500">
                                          Attenzione
                                      </h3>
                                      <div className="mt-2">
                                          <p className="text-lg text-gray-500">
                                              {azione === 'RESETTA' && `Confermi il ripristino dell'elaborazione delle richieste?`}
                                              {azione === 'ACCETTA' && `Confermi l'accettazione delle ${numRichieste} richieste?`}
                                              {azione === 'RESPINGI' && `Confermi il respingimento delle ${numRichieste} richieste?`}
                                          </p>
                                      </div>
                                  </div>
                              </div>
                              }
                ></ConfirmModal>
            </div>
        </Loading>
    </>;
}

export default MonitorWorkflow;